import { createSelector } from '@ngrx/store';
import { adapter, RuleState } from '../reducers/rules.reducer';
import { AlarmStatus } from '../../../enum/status.enum';
import { EntityState } from '@ngrx/entity';
import { Rule } from '../../models/rule.model';
import { AlarmDistributionDataset } from '../../models/alarm-distribution.model';
import { SEVERITY_COLOR_MAP } from '../../../utils/color.utils';

export const selectRulesState = (state: any) => state.rules;

const { selectAll, selectEntities } = adapter.getSelectors();

// export const selectRules = createSelector(selectRulesState, selectAll);
export const selectRules = createSelector(
  selectRulesState,
  (state: EntityState<Rule>) => {
    const allRules = Object.values(state.entities).filter(
      (r): r is Rule => !!r,
    );
    return allRules.sort((a, b) => {
      const dateA = a.latestAlarm?.startDate
        ? new Date(a.latestAlarm.startDate)
        : null;
      const dateB = b.latestAlarm?.startDate
        ? new Date(b.latestAlarm.startDate)
        : null;
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateB.getTime() - dateA.getTime();
    });
  },
);
export const selectRulesEntities = createSelector(
  selectRulesState,
  selectEntities,
);

export const selectRuleByUid = (ruleUid: string) =>
  createSelector(selectRules, (rules) =>
    rules.find((rule) => rule.uid === ruleUid),
  );

export const selectFilters = createSelector(
  selectRulesState,
  (state: RuleState) => state.filters,
);

export const selectCriticalityOptions = createSelector(
  selectRulesState,
  (state: RuleState) => state.criticalityOptions,
);

export const selectActiveRules = createSelector(selectRules, (rules) => {
  return rules.filter((rule) => {
    return rule?.latestAlarm?.status === AlarmStatus.Enabled;
  });
});

export const selectRulesLoading = createSelector(
  selectRulesState,
  (state) => state.loading,
);
export const selectRulesError = createSelector(
  selectRulesState,
  (state) => state.error,
);

export const selectPagedRules = createSelector(
  selectRulesState,
  (state: RuleState) => state.pagedRules ?? [],
);

export const selectPagedTotal = createSelector(
  selectRulesState,
  (state: RuleState) => state.pagedTotal ?? 0,
);

export const selectRulesPageLoading = createSelector(
  selectRulesState,
  (state: RuleState) => state.loadingPage,
);

export const selectLoadingUpdateRule = createSelector(
  selectRulesState,
  (state: RuleState) => state.loadingUpdateRule,
);

export const selectIsCreateOrUpdateRuleOk = createSelector(
  selectRulesState,
  (state: RuleState) => state.isCreateOrUpdateRuleOk,
);

export const selectRuleCreating = createSelector(
  selectRulesState,
  (s) => s.creating,
);

export const selectRuleCreateErrorMessage = createSelector(
  selectRulesState,
  (s) => s.createErrorMessage,
);

export const selectRuleUpdateErrorMessage = createSelector(
  selectRulesState,
  (s) => s.updateErrorMessage,
);

export const selectRuleStatistics = createSelector(
  selectRulesState,
  (state) => state.statistics,
);

export const selectRuleStatisticsLoading = createSelector(
  selectRulesState,
  (state) => state.loadingStatistics,
);

export const selectRuleStatisticsError = createSelector(
  selectRulesState,
  (state) => state.statisticsError,
);

export const selectFailCount = createSelector(
  selectRuleStatistics,
  (statistics) => statistics?.failCount || 0,
);

export const selectWorkingCount = createSelector(
  selectRuleStatistics,
  (statistics) => statistics?.workingCount || 0,
);

export const selectAlarmStatistics = createSelector(
  selectRulesState,
  (state) => state.alarmStatistics,
);

export const selectAlarmStatisticsLoading = createSelector(
  selectRulesState,
  (state) => state.loadingAlarmStatistics,
);

export const selectAlarmStatisticsError = createSelector(
  selectRulesState,
  (state) => state.alarmStatisticsError,
);

export const selectAlarmCount = createSelector(
  selectAlarmStatistics,
  (alarmStatistics) => alarmStatistics?.alarmCount || 0,
);

export const selectActiveAlarmsFromYesterday = createSelector(
  selectAlarmStatistics,
  (alarmStatistics) => alarmStatistics?.activeAlarmsFromYesterday || 0,
);

export const selectAlarmDistribution = createSelector(
  selectRulesState,
  (state) => state.alarmDistribution,
);

export const selectAlarmDistributionLoading = createSelector(
  selectRulesState,
  (state) => state.loadingAlarmDistribution,
);

export const selectAlarmDistributionError = createSelector(
  selectRulesState,
  (state) => state.alarmDistributionError,
);
